/**
 * Preview audio: plays the engine's mix plan with WebAudio. The gain envelopes (volume, fades,
 * ducking) are the exact points the render worker applies, so what you hear is what renders.
 */

import { buildMixPlan, gainAt, type Project } from "@studio/engine";

export class AudioPlayer {
  private ctx: AudioContext | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private loading = new Map<string, Promise<void>>();
  private nodes: AudioScheduledSourceNode[] = [];
  private t0 = 0;
  private ctxStart = 0;
  private master: GainNode | null = null;
  private volume = 1;

  constructor(private base: string) {}

  private context(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext({ latencyHint: "interactive" });
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(this.ctx.destination);
    }
    return this.ctx;
  }

  /** Listening volume (0–1) on top of the mix; it doesn't change the project or the render. */
  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this.volume;
  }

  /** Decode every audio asset used by clips (once). */
  ensure(project: Project): Promise<void> {
    const ids = new Set(project.audio.map((c) => c.assetId));
    const work: Promise<void>[] = [];
    for (const a of project.assets) {
      if (a.kind !== "audio" || !ids.has(a.id) || this.buffers.has(a.id)) continue;
      let p = this.loading.get(a.id);
      if (!p) {
        p = fetch(`${this.base}/${a.file}`)
          .then((r) => r.arrayBuffer())
          .then((b) => this.context().decodeAudioData(b))
          .then((buf) => void this.buffers.set(a.id, buf))
          .catch((e) => console.warn("audio decode failed", a.file, e))
          .finally(() => this.loading.delete(a.id));
        this.loading.set(a.id, p);
      }
      work.push(p);
    }
    return Promise.all(work).then(() => undefined);
  }

  async start(project: Project, t0: number, duration: number): Promise<void> {
    this.stop();
    const ctx = this.context();
    if (ctx.state === "suspended") await ctx.resume();
    const plan = buildMixPlan(project, duration);
    const now = ctx.currentTime + 0.05;
    this.t0 = t0;
    this.ctxStart = now;
    for (const c of plan.clips) {
      const buf = this.buffers.get(c.assetId);
      const end = c.start + c.duration;
      if (!buf || end <= t0) continue;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const g = ctx.createGain();
      // envelope relative to the context clock
      const at = (t: number) => now + (t - t0);
      g.gain.setValueAtTime(gainAt(c.gain, Math.max(t0, c.start)), now);
      for (const [t, v] of c.gain) if (t > t0) g.gain.linearRampToValueAtTime(v, at(t));
      src.connect(g).connect(this.master!);
      const startAt = Math.max(0, c.start - t0);
      const offset = c.offset + Math.max(0, t0 - c.start);
      src.start(now + startAt, offset, end - Math.max(t0, c.start));
      this.nodes.push(src);
    }
  }

  stop(): void {
    for (const n of this.nodes) {
      try {
        n.stop();
      } catch {
        /* already stopped */
      }
    }
    this.nodes = [];
  }

  /** Current timeline time while playing. */
  now(): number {
    return this.ctx ? this.t0 + Math.max(0, this.ctx.currentTime - this.ctxStart) : this.t0;
  }

  buffer(assetId: string): AudioBuffer | undefined {
    return this.buffers.get(assetId);
  }
}
